// Orchestrator API client
// Uses log-server proxy endpoints to access orchestrator (3849)
// Browser can't reach localhost:3849 directly - must go through same-origin proxy

const ORCH_BASE = '/api/orchestrator';

export type Phase =
  | 'CONSTRAINTS'
  | 'INTENT'
  | 'MECHANICS'
  | 'INFERENCE'
  | 'ACCEPTANCE'
  | 'CONTROL_PLANE'
  | 'EXECUTION';

export type Mode = 'NORMAL' | 'INTERROGATION' | 'ANALYSIS' | 'EXECUTION';

export type JudgmentVerdict = 'PENDING' | 'PASS' | 'FAIL' | 'UNAVAILABLE';

export interface OrchestratorState {
  session_id: string;
  phase: Phase;
  mode: Mode;
  deviation_count: number;
  warning_count: number;
  phase_iteration: number;
  execution_confirmed: boolean;
  has_frozen_intent: boolean;
  has_acceptance_criteria: boolean;
  last_correction_detected: boolean;
  judgment_enabled: boolean;
  judgment_pending: boolean;
  judgment_verdict: JudgmentVerdict | null;
}

export interface JudgmentGate {
  enabled: boolean;
  verdict: string;
}

export interface NextStep {
  phase: Phase;
  mode: Mode;
  directive_type: string;
  advisory: string | null;
  blocked_reason: string | null;
  requires_confirmation: boolean;
}

export interface FrozenIntent {
  clarified_goal: string;
  constraints: string[];
  non_goals: string[];
  assumptions: string[];
  confidence: number;
  timestamp?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

class OrchestratorAPI {
  private sessionId: string;

  constructor(sessionId?: string) {
    // Use provided sessionId or generate a fallback
    this.sessionId = sessionId || `ui-${Date.now()}`;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
  }

  async getState(): Promise<OrchestratorState | null> {
    try {
      const sessionId = this.getSessionId();
      const res = await fetch(`${ORCH_BASE}/state/${sessionId}`);
      if (!res.ok) return null;
      const data: ApiResponse<OrchestratorState> = await res.json();
      return data.data || null;
    } catch (e) {
      console.error('Failed to get orchestrator state:', e);
      return null;
    }
  }

  async getNextStep(): Promise<NextStep | null> {
    try {
      const sessionId = this.getSessionId();
      const res = await fetch(`${ORCH_BASE}/next-step/${sessionId}`);
      if (!res.ok) return null;
      const data: ApiResponse<NextStep> = await res.json();
      return data.data || null;
    } catch (e) {
      console.error('Failed to get next step:', e);
      return null;
    }
  }

  async getFrozenIntent(): Promise<FrozenIntent | null> {
    try {
      const sessionId = this.getSessionId();
      const res = await fetch(`${ORCH_BASE}/frozen-intent/${sessionId}`);
      // 404 means no intent yet - that's OK, not an error
      if (res.status === 404) return null;
      if (!res.ok) return null;
      const data: ApiResponse<FrozenIntent> = await res.json();
      return data.data || null;
    } catch (e) {
      console.error('Failed to get frozen intent:', e);
      return null;
    }
  }

  async setMode(mode: Mode): Promise<boolean> {
    try {
      const sessionId = this.getSessionId();
      const res = await fetch(`${ORCH_BASE}/set-mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, mode }),
      });
      const data: ApiResponse<void> = await res.json();
      return data.success;
    } catch (e) {
      console.error('Failed to set mode:', e);
      return false;
    }
  }

  async advancePhase(): Promise<{ success: boolean; phase?: Phase; error?: string }> {
    try {
      const sessionId = this.getSessionId();
      const res = await fetch(`${ORCH_BASE}/advance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data = await res.json();
      if (data.success) {
        return { success: true, phase: data.data?.new_phase };
      }
      return { success: false, error: data.error };
    } catch (e) {
      console.error('Failed to advance phase:', e);
      return { success: false, error: 'Network error' };
    }
  }

  async regressPhase(): Promise<{ success: boolean; phase?: Phase; error?: string }> {
    try {
      const sessionId = this.getSessionId();
      const res = await fetch(`${ORCH_BASE}/regress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data = await res.json();
      if (data.success) {
        return { success: true, phase: data.data?.new_phase };
      }
      return { success: false, error: data.error };
    } catch (e) {
      console.error('Failed to regress phase:', e);
      return { success: false, error: 'Network error' };
    }
  }

  async reset(): Promise<boolean> {
    try {
      const sessionId = this.getSessionId();
      const res = await fetch(`${ORCH_BASE}/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data: ApiResponse<OrchestratorState> = await res.json();
      return data.success;
    } catch (e) {
      console.error('Failed to reset:', e);
      return false;
    }
  }

  async setFrozenIntent(hasIntent: boolean): Promise<boolean> {
    try {
      const sessionId = this.getSessionId();
      const res = await fetch(`${ORCH_BASE}/set-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, has_intent: hasIntent }),
      });
      const data: ApiResponse<void> = await res.json();
      return data.success;
    } catch (e) {
      console.error('Failed to set frozen intent:', e);
      return false;
    }
  }

  async setAcceptanceCriteria(hasCriteria: boolean): Promise<boolean> {
    try {
      const sessionId = this.getSessionId();
      const res = await fetch(`${ORCH_BASE}/set-acceptance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, has_criteria: hasCriteria }),
      });
      const data: ApiResponse<void> = await res.json();
      return data.success;
    } catch (e) {
      console.error('Failed to set acceptance criteria:', e);
      return false;
    }
  }

  async confirmExecution(): Promise<{ success: boolean; error?: string }> {
    try {
      const sessionId = this.getSessionId();
      const res = await fetch(`${ORCH_BASE}/confirm-execution`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data = await res.json();
      return { success: data.success, error: data.error };
    } catch (e) {
      console.error('Failed to confirm execution:', e);
      return { success: false, error: 'Network error' };
    }
  }

  async prepareExecution(): Promise<{ success: boolean; phase?: Phase; error?: string }> {
    try {
      // First switch to EXECUTION mode
      await this.setMode('EXECUTION');

      // Then advance phases until we reach CONTROL_PLANE
      let currentPhase: Phase | undefined;
      const targetPhase: Phase = 'CONTROL_PLANE';
      const maxIterations = 10; // Safety limit

      for (let i = 0; i < maxIterations; i++) {
        const state = await this.getState();
        if (!state) {
          return { success: false, error: 'Failed to get state' };
        }

        currentPhase = state.phase;

        // Check if we've reached target
        if (currentPhase === targetPhase) {
          return { success: true, phase: currentPhase };
        }

        // Advance to next phase
        const result = await this.advancePhase();
        if (!result.success) {
          return { success: false, error: result.error || 'Failed to advance phase' };
        }
      }

      return { success: false, error: 'Max iterations reached' };
    } catch (e) {
      console.error('Failed to prepare execution:', e);
      return { success: false, error: 'Network error' };
    }
  }

  async health(): Promise<boolean> {
    try {
      const res = await fetch(`${ORCH_BASE}/health`);
      if (!res.ok) return false;
      const data = await res.json();
      return data.status === 'ok';
    } catch {
      return false;
    }
  }

  // === JUDGMENT GATE METHODS ===

  async getJudgmentGate(): Promise<JudgmentGate | null> {
    try {
      const sessionId = this.getSessionId();
      const res = await fetch(`${ORCH_BASE}/judgment/${sessionId}`);
      if (!res.ok) return null;
      // Returns {enabled, verdict} directly, not wrapped
      const data: JudgmentGate = await res.json();
      return data;
    } catch (e) {
      console.error('Failed to get judgment gate:', e);
      return null;
    }
  }

  async setJudgmentEnabled(enabled: boolean): Promise<JudgmentGate | null> {
    try {
      const sessionId = this.getSessionId();
      const res = await fetch(`${ORCH_BASE}/judgment-enabled`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, enabled }),
      });
      if (!res.ok) return null;
      const data: ApiResponse<JudgmentGate> = await res.json();
      return data.data || null;
    } catch (e) {
      console.error('Failed to set judgment enabled:', e);
      return null;
    }
  }

  async setJudgmentVerdict(verdict: JudgmentVerdict): Promise<JudgmentGate | null> {
    try {
      const sessionId = this.getSessionId();
      const res = await fetch(`${ORCH_BASE}/judgment-verdict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, verdict }),
      });
      if (!res.ok) return null;
      const data: ApiResponse<JudgmentGate> = await res.json();
      return data.data || null;
    } catch (e) {
      console.error('Failed to set judgment verdict:', e);
      return null;
    }
  }
}

// Factory function - create instance per terminal/session
export const createOrchestratorAPI = (sessionId: string): OrchestratorAPI => {
  return new OrchestratorAPI(sessionId);
};

// Export class for typing
export { OrchestratorAPI };

// === AOG (AUTONOMOUS ORCHESTRATION GOVERNOR) TYPES ===

export type AogRunState = 'pending' | 'frozen' | 'executing' | 'halted' | 'completed' | 'abandoned';

export interface AogRun {
  run_id: string;
  state: AogRunState;
  intent: string;
  constraints: string; // JSON array string
  non_goals: string;
  assumptions: string;
  acceptance_criteria: string;
  aec_hash: string;
  last_step_id: string;
  deny_count: number;
  halt_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface AogAuditEntry {
  id: number;
  run_id: string;
  step_id: string;
  tool: string;
  args: string;
  decision: 'ALLOW' | 'DENY' | 'HALT';
  reason: string | null;
  signature: string;
  snapshot_id: string | null;
  timestamp: string;
}

export interface FreezeRequest {
  intent: string;
  constraints?: string[];
  non_goals?: string[];
  acceptance_criteria?: string[];
  assumptions?: string[];
  session_id?: string;
}

export interface FreezeResponse {
  run_id: string;
  hash: string;
  created_at: string;
}

// === AOG API CLASS ===

class AogAPI {
  private baseUrl = ORCH_BASE;

  async freezeAec(request: FreezeRequest): Promise<FreezeResponse | null> {
    try {
      const res = await fetch(`${this.baseUrl}/aec/freeze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });
      if (!res.ok) return null;
      const data: ApiResponse<FreezeResponse> = await res.json();
      return data.data || null;
    } catch (e) {
      console.error('Failed to freeze AEC:', e);
      return null;
    }
  }

  async getRun(runId: string): Promise<AogRun | null> {
    try {
      const res = await fetch(`${this.baseUrl}/run/${runId}`);
      if (!res.ok) return null;
      const data: ApiResponse<AogRun> = await res.json();
      return data.data || null;
    } catch (e) {
      console.error('Failed to get AOG run:', e);
      return null;
    }
  }

  async haltRun(runId: string, reason = 'manual_halt'): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/run/${runId}/halt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const data: ApiResponse<{ halted: boolean }> = await res.json();
      return data.success;
    } catch (e) {
      console.error('Failed to halt AOG run:', e);
      return false;
    }
  }

  async completeRun(runId: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/run/${runId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data: ApiResponse<{ completed: boolean }> = await res.json();
      return data.success;
    } catch (e) {
      console.error('Failed to complete AOG run:', e);
      return false;
    }
  }

  async getAuditLog(runId: string): Promise<AogAuditEntry[]> {
    try {
      const res = await fetch(`${this.baseUrl}/run/${runId}/audit`);
      if (!res.ok) return [];
      const data: ApiResponse<AogAuditEntry[]> = await res.json();
      return data.data || [];
    } catch (e) {
      console.error('Failed to get AOG audit log:', e);
      return [];
    }
  }

  async getRecentRuns(): Promise<AogRun[]> {
    try {
      const res = await fetch(`${this.baseUrl}/runs/recent`);
      if (!res.ok) return [];
      const data: ApiResponse<AogRun[]> = await res.json();
      return data.data || [];
    } catch (e) {
      console.error('Failed to get recent AOG runs:', e);
      return [];
    }
  }
}

// Export AOG API singleton
export const aogAPI = new AogAPI();

// Default singleton for backwards compatibility (uses auto-generated session)
export const orchestratorAPI = new OrchestratorAPI();
